<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container remote-work-navigation">
      <oxd-text tag="h6" class="orangehrm-main-title">Remote Work</oxd-text>
      <oxd-text class="remote-work-introduction">
        Request approval to work away from your designated workplace. Remote
        work does not use your leave balance.
      </oxd-text>
      <div class="remote-work-tabs" aria-label="Remote work views">
        <oxd-button
          label="Apply for Remote Work"
          :display-type="view === 'apply' ? 'secondary' : 'ghost'"
          :disabled="!ready"
          @click="showApply"
        />
        <oxd-button
          label="My Requests"
          :display-type="view === 'mine' ? 'secondary' : 'ghost'"
          :disabled="!ready"
          @click="showRequests('mine')"
        />
        <oxd-button
          v-if="canReviewTeam"
          label="Team Requests"
          :display-type="view === 'team' ? 'secondary' : 'ghost'"
          @click="showRequests('team')"
        />
      </div>
      <oxd-text v-if="!ready && !error" class="remote-work-message">
        Loading remote work settings…
      </oxd-text>
      <div v-if="error" role="alert" class="remote-work-message">
        <oxd-text>{{ error }}</oxd-text>
        <oxd-button
          label="Try again"
          display-type="ghost"
          @click="loadSettings"
        />
      </div>
      <oxd-text
        v-if="ready && !executiveDirectorConfigured"
        class="remote-work-message"
        role="status"
      >
        An administrator must set up the Executive Director approver before
        remote work requests can be submitted.
      </oxd-text>
    </div>
    <remote-work-apply
      v-if="ready && view === 'apply'"
      :key="revision ? revision.id : 'new'"
      :request="revision"
      :policy-version="policyVersion"
      :can-submit="executiveDirectorConfigured"
      @submitted="onSubmitted"
      @cancel="showRequests('mine')"
    />
    <remote-work-requests
      v-if="ready && view !== 'apply'"
      :key="view"
      :scope="view"
      @revise="onRevise"
      @metadata="onMetadata"
    />
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import RemoteWorkApply from './RemoteWorkApply.vue';
import RemoteWorkRequests from './RemoteWorkRequests.vue';
import {
  REMOTE_WORK_PATH,
  REMOTE_WORK_POLICY,
  remoteWorkError,
} from '@/orangehrmLeavePlugin/util/remote-work';

export default {
  name: 'RemoteWork',
  components: {
    'remote-work-apply': RemoteWorkApply,
    'remote-work-requests': RemoteWorkRequests,
  },
  props: {
    defaultScope: {type: String, default: 'mine'},
    initialView: {type: String, default: 'requests'},
  },
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, REMOTE_WORK_PATH);
    http.setIgnorePath(REMOTE_WORK_PATH);
    return {http};
  },
  data() {
    return {
      ready: false,
      error: '',
      view: 'mine',
      revision: null,
      canReviewTeam: false,
      executiveDirectorConfigured: false,
      policyVersion: REMOTE_WORK_POLICY,
    };
  },
  beforeMount() {
    this.loadSettings();
  },
  methods: {
    async loadSettings() {
      this.error = '';
      try {
        const {data} = await this.http.getAll({scope: 'mine', limit: 1});
        this.onMetadata(data.meta);
        this.view =
          this.initialView === 'apply'
            ? 'apply'
            : this.defaultScope === 'team' && this.canReviewTeam
            ? 'team'
            : 'mine';
        this.ready = true;
      } catch (error) {
        this.error = remoteWorkError(
          error,
          'Remote work settings could not be loaded. Please try again.',
        );
      }
    },
    onMetadata(meta = {}) {
      this.canReviewTeam = meta.canReviewTeam === true;
      this.executiveDirectorConfigured =
        meta.executiveDirectorConfigured === true;
      this.policyVersion = meta.policyVersion || REMOTE_WORK_POLICY;
    },
    showApply() {
      this.revision = null;
      this.view = 'apply';
    },
    showRequests(scope) {
      this.revision = null;
      this.view = scope === 'team' && this.canReviewTeam ? 'team' : 'mine';
    },
    onSubmitted() {
      this.showRequests('mine');
    },
    onRevise(request) {
      this.revision = request;
      this.view = 'apply';
    },
  },
};
</script>

<style lang="scss" scoped>
.remote-work-navigation {
  margin-bottom: 1rem;
}
.remote-work-introduction {
  margin: 0.75rem 0 1rem;
}
.remote-work-tabs {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.remote-work-message {
  margin-top: 1rem;
  padding: 1rem;
  border-left: 3px solid $oxd-primary-one-color;
  background: $oxd-background-pastel-white-color;
}
</style>
